import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { useToastStore } from '@/stores';
import type { ToastData, ToastType } from '@/stores';

interface ToastProps {
    toast: ToastData;
}

const TYPE_STYLES: Record<
    ToastType,
    { border: string; glow: string; text: string; icon: React.ReactNode }
> = {
    success: {
        border: 'border-border',
        glow: 'shadow-[0_8px_32px_rgba(0,0,0,0.5)]',
        text: 'text-primary',
        icon: <CheckCircle2 size={20} />,
    },
    error: {
        border: 'border-destructive/40',
        glow: 'shadow-[0_8px_32px_rgba(0,0,0,0.5)]',
        text: 'text-destructive',
        icon: <XCircle size={20} />,
    },
    warning: {
        border: 'border-border',
        glow: 'shadow-[0_8px_32px_rgba(0,0,0,0.5)]',
        text: 'text-amber-400',
        icon: <AlertTriangle size={20} />,
    },
    info: {
        border: 'border-border',
        glow: 'shadow-[0_8px_32px_rgba(0,0,0,0.5)]',
        text: 'text-info',
        icon: <Info size={20} />,
    },
};

const Toast = ({ toast }: ToastProps) => {
    const dismiss = useToastStore((s) => s.dismiss);
    const [isDismissing, setIsDismissing] = useState(false);
    // Hover/focus pauses auto-dismiss (WCAG 2.2.1) — the timer restarts in
    // full when the pointer/focus leaves.
    const [isPaused, setIsPaused] = useState(false);
    const onCloseRef = useRef<() => void>(() => {});

    const handleClose = React.useCallback(() => {
        setIsDismissing(true);
        setTimeout(() => dismiss(toast.id), 200);
    }, [dismiss, toast.id]);

    useEffect(() => {
        onCloseRef.current = handleClose;
    }, [handleClose]);

    useEffect(() => {
        if (isPaused) return;
        const timer = setTimeout(
            () => onCloseRef.current(),
            toast.duration ?? 5000,
        );
        return () => clearTimeout(timer);
    }, [toast.duration, isPaused]);

    const pauseHandlers = {
        onMouseEnter: () => setIsPaused(true),
        onMouseLeave: () => setIsPaused(false),
        onFocus: () => setIsPaused(true),
        onBlur: () => setIsPaused(false),
    };

    const styles = TYPE_STYLES[toast.type];

    if (toast.render) {
        return (
            <div
                {...pauseHandlers}
                className={cn(
                    isDismissing
                        ? 'animate-slide-up-fade-out'
                        : 'animate-slide-down-fade',
                )}
            >
                {toast.render({ toast, onClose: handleClose })}
            </div>
        );
    }

    return (
        <button
            onClick={handleClose}
            {...pauseHandlers}
            className={cn(
                'flex cursor-pointer items-start gap-3 rounded-xl border bg-card/95 px-4 py-3 backdrop-blur-sm transition-all hover:scale-[1.02]',
                styles.border,
                styles.glow,
                isDismissing
                    ? 'animate-slide-up-fade-out'
                    : 'animate-slide-down-fade',
            )}
        >
            <span className={cn('mt-0.5 shrink-0', styles.text)}>
                {styles.icon}
            </span>

            <div className="flex flex-col items-start text-left">
                <span
                    className={cn(
                        'font-sans text-sm font-semibold',
                        styles.text,
                    )}
                >
                    {toast.message}
                </span>
                {toast.description && (
                    <span className="font-sans text-xs text-muted-foreground">
                        {toast.description}
                    </span>
                )}
            </div>
            <span className="sr-only">, dismiss</span>
        </button>
    );
};

Toast.displayName = 'Toast';

export { Toast };
export type { ToastProps };
